import { useCallback } from 'react';
import { apiFetch } from '../utils/apiUtils';

/**
 * Custom hook for handling field save logic with proper rules and validation
 * 
 * Rules:
 * - Matched names should save
 * - Unmatched names should save  
 * - If no changes to priority order, NO default should be saved
 * - If someone hits clear overrides OR resets values to match current default override, that override should be removed
 * - All prompt values should be saved
 * - Should be sent as UUID value to avoid field name lookups
 */
const useFieldSaveLogic = () => {
  
  /**
   * Determines if an override should be removed based on whether the changes match the default override
   * @param {Object} changes - The field changes being made
   * @param {Object} defaultOverride - The current default override for this field
   * @returns {boolean} - Whether the override should be removed
   */
  const shouldRemoveOverride = useCallback((changes, defaultOverride) => {
    if (!defaultOverride) {
      return false;
    }

    // Check if all the key properties match the default override
    const matchesDefault = (
      changes.status === defaultOverride.status &&
      changes.priority === defaultOverride.priority &&
      changes.document_mapping === defaultOverride.document_mapping &&
      changes.matching_hints === defaultOverride.matching_hints
    );

    return matchesDefault;
  }, []);

  /**
   * Builds the payload for saving field changes
   * @param {Object} changes - The field changes
   * @param {Object} field - The original field object
   * @param {boolean} removeOverride - Whether to remove the override
   * @returns {Object} - The payload to send to the API
   */
  const buildSavePayload = useCallback((changes, field, removeOverride) => {
    const payload = {
      field_uuid: field.field_uuid, // Use UUID instead of field name lookups
      status: changes.status,
      priority: changes.priority,
      document_mapping: changes.document_mapping,
      matching_hints: changes.matching_hints || '',
    };

    // Handle prompt_name logic
    if (changes.fieldName === field.displayName) {
      // Matched names - use existing prompt_name
      payload.prompt_name = field.prompt_name;
    } else {
      // Unmatched names - use the new field name as prompt_name
      payload.prompt_name = changes.fieldName;
    }

    // Add remove_override flag if needed
    if (removeOverride) {
      payload.remove_override = true;
    }

    return payload;
  }, []);

  /**
   * Saves field changes with proper validation and error handling
   * @param {Object} changes - The field changes to save
   * @param {Object} field - The original field object
   * @param {Object} defaultOverrides - Map of default overrides by field UUID
   * @returns {Promise<Object>} - The result of the save operation
   */
  // Simple status toggle - just enable/disable without touching other settings
  const toggleFieldStatus = useCallback(async (field, newStatus) => {
    try {
      // apiFetch already handles errors and returns parsed JSON directly
      const result = await apiFetch(`/system-files/fields/${field.field_uuid}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status: newStatus }),
      });

      console.log(`✅ Field ${field.displayName} status updated to ${newStatus}`);
      return { success: true, message: result.message };
    } catch (error) {
      console.error(`❌ Error updating field status: ${error.message}`);
      return { success: false, error: error.message };
    }
  }, []);

  const saveFieldChanges = useCallback(async (changes, field, defaultOverrides = {}) => {
    try {
      // Get the default override for this field
      const defaultOverride = defaultOverrides[field.field_uuid];
      
      // Check if we should remove the override
      const removeOverride = changes.clearOverrides || shouldRemoveOverride(changes, defaultOverride);
      
      // Build the payload
      const payload = buildSavePayload(changes, field, removeOverride);
      
      // Make the API call
      // apiFetch already handles errors and returns parsed JSON directly
      const result = await apiFetch('/system-files/fields/update', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to save field changes');
      }

      console.log(`✅ Field ${field.displayName} changes saved successfully`);
      
      return {
        success: true,
        data: result.data,
        removedOverride: removeOverride
      };

    } catch (error) {
      console.error('❌ Error saving field changes:', error);
      
      return {
        success: false,
        error: error.message
      };
    }
  }, [shouldRemoveOverride, buildSavePayload]);

  /**
   * Validates field changes before saving
   * @param {Object} changes - The field changes to validate
   * @param {Object} field - The original field object
   * @returns {Object} - Validation result with success flag and any errors
   */
  const validateFieldChanges = useCallback((changes, field) => {
    const errors = [];

    // Validate required fields
    if (!changes.fieldName || changes.fieldName.trim() === '') {
      errors.push('Field name is required');
    }

    if (!changes.status || !['active', 'ignored'].includes(changes.status)) {
      errors.push('Valid status is required');
    }

    if (changes.priority === undefined || changes.priority === null || changes.priority < 1) {
      errors.push('Valid priority is required');
    }

    // Document mapping is optional - can be empty string for "any" mapping
    // Only validate if it's explicitly set to a non-string value (backend will handle valid document type validation)
    if (changes.document_mapping !== undefined && changes.document_mapping !== null && 
        changes.document_mapping !== '' && typeof changes.document_mapping !== 'string') {
      errors.push('Document mapping must be a valid string value');
    }

    // Validate field object
    if (!field || !field.field_uuid) {
      errors.push('Valid field object with UUID is required');
    }

    return {
      success: errors.length === 0,
      errors
    };
  }, []);

  /**
   * Saves field changes with validation
   * @param {Object} changes - The field changes to save
   * @param {Object} field - The original field object
   * @param {Object} defaultOverrides - Map of default overrides by field UUID
   * @returns {Promise<Object>} - The result of the save operation
   */
  const saveFieldChangesWithValidation = useCallback(async (changes, field, defaultOverrides = {}) => {
    // Validate changes first
    const validation = validateFieldChanges(changes, field);
    
    if (!validation.success) {
      return {
        success: false,
        error: `Validation failed: ${validation.errors.join(', ')}`,
        validationErrors: validation.errors
      };
    }

    // Proceed with save
    return await saveFieldChanges(changes, field, defaultOverrides);
  }, [validateFieldChanges, saveFieldChanges]);

  /**
   * Saves modal settings (field properties + document priority) with proper validation
   * @param {Object} settings - The modal settings to save
   * @param {Object} field - The original field object
   * @param {Object} documents - Array of document objects from modal
   * @returns {Promise<Object>} - The result of the save operation
   */
  const saveModalSettings = useCallback(async (settings, field, documents = []) => {
    try {
      if (!field) {
        return {
          success: false,
          error: 'No field provided'
        };
      }

      let fieldSaveSuccess = true;
      let docPrioritySaveSuccess = true;
      
      // Step 1: Field properties are now handled by saveFieldProperties in saveModalComprehensive
      // Skip field properties save here to avoid duplicate API calls
      fieldSaveSuccess = true;

      // Step 2: Save document priority settings
      // Only include enabled documents in the priority order
      const currentDocumentPriority = documents.filter(doc => doc.enabled).map(doc => doc.id);
      const currentEnabledDocuments = documents.reduce((acc, doc) => {
        acc[doc.id] = doc.enabled;
        return acc;
      }, {});

      // Check if document settings need to be saved
      const fieldUsesDefaults = !field.document_priority || field.document_priority.length === 0;
      const hasDocumentChanges = Boolean(settings.documentPriority || settings.enabledDocuments);
      
      if (hasDocumentChanges) {
        const fieldName = encodeURIComponent(settings.systemName || field.displayName);
        const url = `/api/system-files/fields/${fieldName}/document-priority`;
        const payload = {
          document_priority: currentDocumentPriority,
          enabled_documents: currentEnabledDocuments,
          matching_hints: settings.matchingHints || ''
        };
        
        console.log('🔍 Document Priority Save:', {
          fieldName: settings.systemName || field.displayName,
          encodedFieldName: fieldName,
          url,
          payload
        });
        
        const response = await fetch(url, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        
        console.log('🔍 Document Priority Response:', {
          status: response.status,
          statusText: response.statusText,
          ok: response.ok
        });
        
        if (!response.ok) {
          docPrioritySaveSuccess = false;
          const errorText = await response.text();
          console.error('❌ Failed to save document priority settings:', errorText);
        } else {
          const responseData = await response.json();
          console.log('✅ Document priority save successful:', responseData);
        }
      }

      // Step 3: Return combined result
      const overallSuccess = fieldSaveSuccess && docPrioritySaveSuccess;
      
      return {
        success: overallSuccess,
        fieldSaveSuccess,
        docPrioritySaveSuccess,
        data: {
          fieldPropertiesChanged: fieldSaveSuccess,
          hasDocumentChanges,
          fieldName: settings.systemName || field.displayName,
          documentPriority: currentDocumentPriority,
          enabledDocuments: currentEnabledDocuments
        },
        error: !overallSuccess ? 'Some saves failed' : null
      };

    } catch (error) {
      console.error('❌ Error saving modal settings:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }, [saveFieldChangesWithValidation]);

  /**
   * Handles resetting a field's document priority to defaults by deleting the override
   * @param {Object} field - The field to reset
   * @returns {Promise<Object>} - Result object with success status
   */
  const resetFieldToDefaults = useCallback(async (field) => {
    try {
      if (!field) {
        return { success: false, error: 'No field provided' };
      }

      // Use field_uuid if available, otherwise fall back to field name (for backward compatibility)
      const fieldIdentifier = field.field_uuid || field.displayName || field.name;
      console.log('🔍 Document priority update - field object:', field);
      console.log('🔍 Document priority update - using identifier:', fieldIdentifier, 'type:', typeof fieldIdentifier);
      
      // apiFetch already handles errors and returns parsed JSON directly
      const responseData = await apiFetch(`/system-files/fields/${encodeURIComponent(fieldIdentifier)}/document-priority`, {
        method: 'DELETE'
      });
      
      console.log('🔍 Reset API Response Data:', responseData);
      
      console.log('Field document priority reset to global default (set to NULL in database)');
      
      // Note: We do NOT delete the field record, we just set document_priority and enabled_documents to NULL
      // The field object will be updated when the parent component refreshes the field list
      
      return { success: true, data: responseData };
      
    } catch (error) {
      console.error('Error resetting field to default:', error);
      return { success: false, error: error.message };
    }
  }, []);

  /**
   * Saves field properties (system name, prompt name, calculated field, priority flag)
   * @param {Object} field - The field object
   * @param {Object} changes - The changes to save
   * @returns {Promise<Object>} - Result object with success status
   */
  const saveFieldProperties = useCallback(async (field, changes) => {
    try {
      if (!field) {
        return { success: false, error: 'No field provided' };
      }

      const {
        systemName,
        promptName,
        isCalculatedField,
        priorityFlag,
        systemNameChanged,
        promptNameChanged,
        calculatedFieldChanged,
        priorityFlagChanged,
        systemNameDifferentFromOriginal
      } = changes;

      const hasUserChanges = systemNameChanged || promptNameChanged || calculatedFieldChanged || priorityFlagChanged || systemNameDifferentFromOriginal;
      
      if (!hasUserChanges) {
        return { success: true, data: { message: 'No changes to save' } };
      }

      console.log('🔍 Changes detected, saving to API...');
      
      // apiFetch already handles errors and returns parsed JSON directly
      const responseData = await apiFetch('/system-files/fields/update', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          field_uuid: field.field_uuid,
          system_name: systemName?.trim(),
          prompt_name: promptName?.trim(),
          is_calculated_field: isCalculatedField,
          priority_flag: priorityFlag
        })
      });
      
      console.log('✅ Field properties saved successfully');
      
      return { success: true, data: responseData };
      
    } catch (error) {
      console.error('Error updating prompt name and field properties:', error);
      return { success: false, error: error.message };
    }
  }, []);

  /**
   * Saves only field properties (for cases where system name/prompt name haven't changed)
   * @param {Object} field - The field object
   * @param {Object} changes - The changes to save
   * @returns {Promise<Object>} - Result object with success status
   */
  const saveFieldPropertiesOnly = useCallback(async (field, changes) => {
    try {
      if (!field) {
        return { success: false, error: 'No field provided' };
      }

      const { isCalculatedField, priorityFlag } = changes;

      console.log('🔍 No changes detected in system name/prompt name, updating field properties only...');
      
      // apiFetch already handles errors and returns parsed JSON directly
      const responseData = await apiFetch('/system-files/fields/field-properties', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          field_uuid: field.field_uuid,
          field_name: field.displayName || field.name, // Keep for backward compatibility
          is_calculated_field: isCalculatedField,
          priority_flag: priorityFlag
        })
      });
      
      console.log('✅ Field properties saved successfully');
      
      return { success: true, data: responseData };
      
    } catch (error) {
      console.error('Error updating field properties:', error);
      return { success: false, error: error.message };
    }
  }, []);

  /**
   * Comprehensive save function that handles all modal save operations
   * @param {Object} settings - The settings from the modal
   * @param {Array} documents - The documents array from the modal
   * @param {Object} field - The field object
   * @param {Object} changeDetection - Object with change detection flags
   * @param {boolean} isResetToDefault - Whether this is a reset operation
   * @param {Array} defaultPriorityOrder - The default document priority order from the system
   * @returns {Promise<Object>} - Result object with success status
   */
  const saveModalComprehensive = useCallback(async (settings, documents, field, changeDetection, isResetToDefault = false, defaultPriorityOrder = []) => {
    try {
      if (!field) {
        return { success: false, error: 'No field provided' };
      }

      // Handle reset to defaults if needed
      if (isResetToDefault) {
        const resetResult = await resetFieldToDefaults(field);
        if (!resetResult.success) {
          return resetResult;
        }
        // Return early - don't send additional payload after reset
        return { success: true, message: 'Field reset to defaults successfully' };
      }

      // Build consolidated payload with ALL field data
      let documentPriority = documents.filter(doc => doc.enabled).map(doc => doc.id);
      const enabledDocuments = documents.reduce((acc, doc) => {
        acc[doc.id] = doc.enabled;
        return acc;
      }, {});
      
      console.log('🔍 DIRTY STATE - documentPriority:', documentPriority);
      console.log('🔍 DIRTY STATE - enabledDocuments:', enabledDocuments);
      console.log('🔍 DIRTY STATE - defaultPriorityOrder:', defaultPriorityOrder);
      
      // Compare document priority with default priority order
      // If they match exactly, send an empty array to avoid storing unnecessary overrides
      if (defaultPriorityOrder && defaultPriorityOrder.length > 0) {
        const arraysEqual = documentPriority.length === defaultPriorityOrder.length &&
          documentPriority.every((val, index) => val === defaultPriorityOrder[index]);
        
        if (arraysEqual) {
          console.log('🔍 Document priority matches default, sending empty array to use system defaults');
          documentPriority = [];
        }
      }

      const consolidatedPayload = {
        field_uuid: field.field_uuid,
        field_name: field.displayName || field.name,
        system_name: settings.systemName,
        prompt_name: settings.promptName,
        is_calculated_field: settings.isCalculatedField,
        priority_flag: settings.priorityFlag,
        status: field.status || 'active', // Required by backend
        priority: field.priority || 5, // Required by backend
        document_mapping: field.documentMapping || 'any', // Required by backend
        document_priority: documentPriority,
        enabled_documents: enabledDocuments,
        matching_hints: settings.matchingHints || '',
        ...changeDetection
      };

      console.log('🔍 Consolidated save payload:', consolidatedPayload);

      // Use the field-properties endpoint that handles is_calculated_field
      // apiFetch already handles errors and returns parsed JSON directly
      const responseData = await apiFetch('/system-files/fields/field-properties', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(consolidatedPayload)
      });

      console.log('✅ Field saved comprehensively:', responseData);
      
      return {
        success: true,
        data: responseData,
        error: null
      };

    } catch (error) {
      console.error('❌ Error in comprehensive modal save:', error);
      return { success: false, error: error.message };
    }
  }, [resetFieldToDefaults]);

  return {
    saveFieldChanges,
    saveFieldChangesWithValidation,
    saveModalSettings,
    saveModalComprehensive,
    resetFieldToDefaults,
    saveFieldProperties,
    saveFieldPropertiesOnly,
    shouldRemoveOverride,
    buildSavePayload,
    validateFieldChanges,
    toggleFieldStatus
  };
};

export default useFieldSaveLogic;
